// Employee/components/View/EventEditModal.tsx
import React, {useEffect, useState} from "react";
import {Event, EventAddData} from "../../types";
import {Box, TextField,} from "@mui/material";
import Modal from "Core/components/elements/Modal/Modal";
import Button from "Core/components/elements/Button/Button";
import {addMinutes} from "date-fns";
import {format as formatTz} from "date-fns-tz";
import {useTheme} from "Theme/ThemeContext";

interface EventEditModalProps {
    event: Event | null;
    open: boolean;
    onClose: () => void;
    onSave: (event: Event) => void;
    userType: string;
    onAdd: (eventData: EventAddData) => void;
    pendingEventAdd: EventAddData | null;
    addEvent: (eventData: EventAddData) => void;
    isMonthView: boolean;
    onDelete: (event: Event) => void;
    timeZone: string;
}

interface VacationModalState {
    isOpen: boolean;
    startDate: string;
    endDate: string;
}

const EventEditModal: React.FC<EventEditModalProps> = ({
                                                           event,
                                                           open,
                                                           onClose,
                                                           onSave,
                                                           userType,
                                                           onAdd,
                                                           pendingEventAdd,
                                                           addEvent,
                                                           isMonthView,
                                                           onDelete,
                                                           timeZone,
                                                       }) => {
    const [editedEvent, setEditedEvent] = useState<Event | null>(null);
    const [selectedTime, setSelectedTime] = useState<string>("");
    const {plt} = useTheme();
    const [weeksToCopy, setWeeksToCopy] = useState<number>(1);
    const [vacationModal, setVacationModal] = useState<VacationModalState>({
        isOpen: false,
        startDate: new Date().toISOString(),
        endDate: '',
    });

    const handleDelete = () => {
        if (editedEvent && userType === "psychologist") {
            onDelete(editedEvent);
            onClose();
        }
    };

    useEffect(() => {
        if (pendingEventAdd) {
            const newEvent: Event = {
                id: Date.now().toString(),
                title: "New Consultation",
                // Convert dates to the user's timezone
                start: new Date(pendingEventAdd.proposedStart).toISOString(),
                end: new Date(pendingEventAdd.proposedEnd).toISOString(),
                status: "NEW",
                color: "#FFA726",
                user: pendingEventAdd.originalEvent.user,
                duration: pendingEventAdd.originalEvent.duration,
            };
            setEditedEvent(newEvent);
            if (isMonthView) {
                setSelectedTime(
                    formatTz(new Date(newEvent.start), "HH:mm", {timeZone})
                );
            }
        } else {
            setEditedEvent(event);
            if (isMonthView && event) {
                setSelectedTime(formatTz(new Date(event.start), "HH:mm", {timeZone}));
            }
        }
    }, [event, pendingEventAdd, isMonthView, timeZone]);

    const handleCopyWeeks = () => {
        if (editedEvent) {
            // Placeholder for API call to copy event
            console.log(`Copying event ${editedEvent.id} for ${weeksToCopy} weeks`);
            // Call the API endpoint here
        }
    };

    const handleSave = () => {
        saveEvent();
    };

    const saveEvent = () => {
        if (editedEvent) {
            if (userType === "client" && pendingEventAdd) {
                if (isMonthView) {
                    const [hours, minutes] = selectedTime.split(":").map(Number);
                    const newStart = new Date(editedEvent.start);
                    newStart.setHours(hours, minutes, 0, 0);
                    const newEnd = addMinutes(
                        newStart,
                        editedEvent.duration?.value || 60
                    );

                    const updatedEventData: EventAddData = {
                        ...pendingEventAdd,
                        proposedStart: newStart,
                        proposedEnd: newEnd,
                    };
                    addEvent(updatedEventData);
                } else {
                    addEvent(pendingEventAdd);
                }
            } else if (userType === "psychologist") {
                onSave(editedEvent);
            }
            onClose();
        }
    };

    const handleCancel = () => {
        setEditedEvent(event);
        onClose();
    };

    const handleVacationClick = () => {
        setVacationModal({
            isOpen: true,
            startDate: new Date().toISOString(),
            endDate: '',
        });
    };

    const handleVacationClose = () => {
        setVacationModal(prev => ({...prev, isOpen: false}));
    };

    const handleVacationSave = () => {
        // TODO: Implement vacation save logic
        console.log('Saving vacation:', vacationModal);
        handleVacationClose();
    };

    if (!editedEvent) return null;

    return (
        <>
            <Modal
                isOpen={open}
                onClose={onClose}
                title=""
                closeBtn={true}
                closeOnOutsideClick={true}
                zIndex={10}
            >
                <Box sx={{
                    p: 4,
                    minWidth: 300,
                    color: 'white',
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: 3
                }}>
                    <TextField
                        fullWidth
                        label="Старт"
                        type="datetime-local"
                        value={formatTz(new Date(editedEvent.start), "yyyy-MM-dd'T'HH:mm", {
                            timeZone,
                        })}
                        onChange={(e) =>
                            setEditedEvent({
                                ...editedEvent,
                                start: new Date(e.target.value).toISOString(),
                            })
                        }
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Конец"
                        type="datetime-local"
                        value={formatTz(new Date(editedEvent.end), "yyyy-MM-dd'T'HH:mm", {
                            timeZone,
                        })}
                        onChange={(e) =>
                            setEditedEvent({
                                ...editedEvent,
                                end: new Date(e.target.value).toISOString(),
                            })
                        }
                        margin="normal"
                    />
                    <Box
                        sx={{display: "flex", gap: 2, mt: 2, mb: 2, alignItems: "center"}}
                    >
                        <TextField
                            label="Недели на копирование"
                            type="number"
                            value={weeksToCopy}
                            onChange={(e) => setWeeksToCopy(Number(e.target.value))}
                            sx={{flexGrow: 1}}
                        />
                        <Button
                            onClick={handleCopyWeeks}
                            variant={'contained'}
                            color="secondary"
                        >
                            Копирование недель
                        </Button>
                    </Box>
                    <Box sx={{display: "flex", justifyContent: "space-between"}}>
                        <Box>
                            <Button
                                onClick={handleDelete}
                                color="warning"
                                sx={{
                                    backgroundColor: plt.error.main,
                                    mr: 1,
                                }}
                            >
                                Удалить
                            </Button>
                            <Button
                                onClick={handleVacationClick}
                                variant="outlined"
                                color="secondary"
                            >
                                Отпуск
                            </Button>
                        </Box>
                        <Box>
                            <Button onClick={handleCancel} sx={{mr: 1}}>
                                Отмена
                            </Button>
                            <Button onClick={handleSave} variant={'contained'} color={'primary'}>
                                Сохранить
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Modal
                isOpen={vacationModal.isOpen}
                onClose={handleVacationClose}
                title="Новый отпуск"
                closeBtn={true}
                closeOnOutsideClick={true}
                zIndex={11}
            >
                <Box sx={{
                    p: 4,
                    minWidth: 300,
                    color: 'white',
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: 3
                }}>
                    <TextField
                        fullWidth
                        label="Начало отпуска"
                        type="datetime-local"
                        value={formatTz(new Date(vacationModal.startDate), "yyyy-MM-dd'T'HH:mm", {
                            timeZone,
                        })}
                        onChange={(e) =>
                            setVacationModal(prev => ({
                                ...prev,
                                startDate: new Date(e.target.value).toISOString(),
                            }))
                        }
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Конец отпуска"
                        type="datetime-local"
                        value={vacationModal.endDate ? formatTz(new Date(vacationModal.endDate), "yyyy-MM-dd'T'HH:mm", {
                            timeZone,
                        }) : ''}
                        onChange={(e) =>
                            setVacationModal(prev => ({
                                ...prev,
                                endDate: new Date(e.target.value).toISOString(),
                            }))
                        }
                        margin="normal"
                    />
                    <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                        <Button onClick={handleVacationClose} sx={{mr: 1}}>
                            Отмена
                        </Button>
                        <Button onClick={handleVacationSave} variant={'contained'} color={'primary'}>
                            Сохранить
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default EventEditModal;
